import { getVideoDuration } from "./get-video-duration";

/* Constants */
export const MAX_DIMENSION = 4096;
export const MAX_VIDEO_SIZE_BYTES = 100 * 1024 * 1024;
export const MAX_VIDEO_DURATION_SECONDS = 180;
export const MAX_VIDEOS_PER_STUDY = 5;

export type MediaType = "image" | "video";

/* Types */
interface ValidateUploadArgs {
  file: File;
  mediaType: MediaType;
  /** Pre-computed dimensions for images (0 × 0 for videos). */
  dimensions: { width: number; height: number };
  /** Videos already occupying a slot in this study (existing + staged). */
  currentVideoCount: number;
}

/**
 * validateUpload function
 * Applies the media-type-specific upload rules for a single file and returns the
 * reason it should be rejected, or null if it is valid. Image studies enforce a max
 * pixel dimension; video studies enforce size, duration, and per-study count caps.
 *
 * @param args.file - The file being staged
 * @param args.mediaType - The study's media type, which selects the rule set
 * @param args.dimensions - Pre-computed image dimensions (ignored for videos)
 * @param args.currentVideoCount - Videos already counted toward the per-study cap
 * @returns A user-facing error message if invalid, otherwise null
 */
export const validateUpload = async ({ file, mediaType, dimensions, currentVideoCount }: ValidateUploadArgs): Promise<string | null> => {
  if (mediaType === "video") {
    if (file.size > MAX_VIDEO_SIZE_BYTES) return "Video must be less than 100MB";
    if (currentVideoCount >= MAX_VIDEOS_PER_STUDY) return `Maximum of ${MAX_VIDEOS_PER_STUDY} videos per study`;

    // Duration requires reading metadata, so only check once the cheaper size and count checks have passed.
    try {
      const duration = await getVideoDuration(file);
      if (duration > MAX_VIDEO_DURATION_SECONDS) return "Video must be 3 minutes or less";
    } catch {
      return "We couldn't read this video, please try again.";
    }

    return null;
  }

  if (dimensions.width > MAX_DIMENSION || dimensions.height > MAX_DIMENSION) {
    return `Image dimensions are larger than ${MAX_DIMENSION} x ${MAX_DIMENSION}px`;
  }

  return null;
};
